import { useMemo, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import {
  IconAlertCircle,
  IconBell,
  IconFileDescription,
  IconPackage,
  IconShieldCheck,
  IconStar,
  IconTruck,
} from "@tabler/icons-react";
import type { IconProps } from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { Button } from "../../../../components/common/button";
import { PreviewListRow } from "../../../../components/common/preview-list-row";
import { StatCard } from "../../../../components/common/stat-card";
import { EmptyState } from "../../../../components/common/empty-state";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { Heading, Text } from "../../../../components/common/typography";
import { Image } from "../../../../components/common/image";
import { useMe } from "../../../../swr";
import { useApi } from "../../../../swr/useApi";
import { loyaltyService } from "../../../api_client";
import type {
  LoyaltyPointsResponseDto,
  NotificationDto,
  SaleDto,
  SaleDetailsDto,
} from "../../../../api";
import { SalesQueryDtoSortOrderEnum } from "../../../../api";
import { useProducts } from "../../../products/hooks/useProducts";
import { ProductCard } from "../../../products/presentation/components/product-card";
import { useWishlistStore } from "../../../products/domain/stores/useWishlistStore";
import {
  computeDiscount,
  getStockLabel,
  getWishlistVariantId,
} from "../../../products/utils/card-props";
import { useOrders } from "../../../sales/hooks/useOrders";
import { useQuotations } from "../../../quotations/hooks/useQuotations";
import { useSaleDetails } from "../../../sales/hooks/useSaleDetails";
import { dollarFormat } from "../../../../utils";
import { deriveOrderStatus } from "../../../sales/utils/order-status";
import { useBuyerWarrantyClaims } from "../../../warranty-claims/hooks/use-buyer-warranty-claims";
import { ClaimStatusChip } from "../../../warranty-claims/presentation/components/claim-status-chip";
import { useMyInbox } from "../../../inbox/hooks/useMyInbox";
import { resolveNotificationLink } from "../../../inbox/resolve-notification-link";
import type { WarrantyClaimDto } from "../../../../api";

const PREVIEW_QUERY = {
  page: 1,
  pageSize: 3,
  sortBy: "create_date",
  sortOrder: SalesQueryDtoSortOrderEnum.Desc,
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useMe();

  const { data: loyaltyResp } = useApi<LoyaltyPointsResponseDto>(
    "dashboard-loyalty-summary",
    () => loyaltyService.getMyLoyaltyPoints({ pageSize: 1 }),
  );
  const { data: allOrdersResp, isLoading: ordersLoading } = useOrders("all", { pageSize: 20, sortBy: "create_date", sortOrder: SalesQueryDtoSortOrderEnum.Desc });
  const { data: quotesResp, isLoading: quotesLoading } = useQuotations("pending", PREVIEW_QUERY);
  const { data: productsData } = useProducts({ page: 1, pageSize: 4 });

  const { data: claimsResp } = useBuyerWarrantyClaims({ pageSize: 3 });
  const { data: inboxResp } = useMyInbox({ pageSize: 3 });
  const totalClaims = claimsResp?.meta?.totalCount ?? 0;
  const recentClaims = (claimsResp?.data ?? []) as WarrantyClaimDto[];
  const recentNotifications = (inboxResp?.data ?? []) as NotificationDto[];

  const firstName = (user?.name ?? "").split(" ")[0] || "there";
  const products = productsData?.data ?? [];

  const wishlistIds = useWishlistStore((s) => s.wishlistVariantIds);
  const toggleWishlist = useWishlistStore((s) => s.toggle);

  const allOrders = allOrdersResp?.data ?? [];
  const totalOrders = allOrdersResp?.meta?.totalCount ?? 0;
  const totalQuotations = quotesResp?.meta?.totalCount ?? 0;

  const { activeOrdersList, attentionOrders, activeCount } = useMemo(() => {
    const active: SaleDto[] = [];
    const attention: SaleDto[] = [];
    for (const s of allOrders) {
      const status = deriveOrderStatus(s);
      if (status === "delivered" && !s.buyerReceived) {
        attention.push(s);
        active.push(s);
      } else if (status === "processing" || status === "inTransit") {
        active.push(s);
      }
    }
    return { activeOrdersList: active, attentionOrders: attention, activeCount: active.length };
  }, [allOrders]);

  const needsAttention = attentionOrders.length;
  const loyaltyPoints = loyaltyResp?.meta?.activePoints ?? 0;

  const recentOrders = activeOrdersList.slice(0, 3);
  const recentQuotes = (quotesResp?.data ?? []).slice(0, 3);

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <Heading variant="h2" tone="dark">
        Hello {firstName}!
      </Heading>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 max-sm:gap-3">
        <StatCard
          icon={<IconPackage size={24} />}
          label="Total Orders"
          value={totalOrders}
          onClick={() => navigate(AppRoutes.ORDERS)}
        />
        <StatCard
          icon={<IconFileDescription size={24} />}
          label="Quotations"
          value={totalQuotations}
          onClick={() => navigate(AppRoutes.QUOTATIONS)}
        />
        <StatCard
          icon={<IconTruck size={24} />}
          label="Active Orders"
          value={activeCount}
          onClick={() => navigate(AppRoutes.ORDERS)}
        />
        <StatCard
          icon={<IconStar size={24} />}
          label="Loyalty Points"
          value={loyaltyPoints.toLocaleString()}
          onClick={() => navigate(AppRoutes.LOYALTY_POINTS)}
        />
        <StatCard
          icon={<IconShieldCheck size={24} />}
          label="Warranty Claims"
          value={totalClaims}
          onClick={() => navigate(AppRoutes.MY_WARRANTY_CLAIMS)}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 max-md:gap-4">
        {needsAttention > 0 && (
          <div className="flex flex-col gap-3 rounded-2xl border border-accentAmber/30 bg-accentAmberTintBg p-6 max-md:p-4 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IconAlertCircle size={20} className="text-accentAmberDark" />
                <Heading variant="h3" tone="dark">Needs Your Attention</Heading>
                <span className="rounded-full bg-accentAmber/20 px-2.5 py-0.5 text-xs font-medium text-accentAmberDark">
                  {needsAttention}
                </span>
              </div>
              <Button variant="underlinedGhostDark" size="sm" onClick={() => navigate(`${AppRoutes.ORDERS}?status=delivered`)}>
                View All
              </Button>
            </div>
            {attentionOrders.slice(0, 3).map((sale) => (
              <PreviewSaleRow key={sale.id} sale={sale} kind="order" onPreview={(id) => navigate(`${AppRoutes.ORDERS}/${id}`)} />
            ))}
          </div>
        )}

        <DashboardSection
          title="Active Orders"
          onViewAll={() => navigate(AppRoutes.ORDERS)}
        >
          <PreviewSaleSection
            sales={recentOrders}
            isLoading={ordersLoading}
            kind="order"
            emptyIcon={IconPackage}
            emptyTitle="You have no active orders."
            onPreview={(id) => navigate(`${AppRoutes.ORDERS}/${id}`)}
          />
        </DashboardSection>

        <DashboardSection
          title="Active Quotations"
          onViewAll={() => navigate(AppRoutes.QUOTATIONS)}
        >
          <PreviewSaleSection
            sales={recentQuotes}
            isLoading={quotesLoading}
            kind="quotation"
            emptyIcon={IconFileDescription}
            emptyTitle="You have no active quotations."
            onPreview={(id) => navigate(AppRoutes.QUOTATION_DETAILS.replace(":id", String(id)))}
          />
        </DashboardSection>

        <DashboardSection
          title="Recent Notifications"
          onViewAll={() => navigate(AppRoutes.INBOX)}
        >
          <RecentNotificationsContent
            notifications={recentNotifications}
            onClickNotification={(n) => {
              const link = resolveNotificationLink(n, { isBuyer: true });
              if (link) navigate(link);
            }}
          />
        </DashboardSection>

        <DashboardSection
          title="Warranty Claims"
          onViewAll={() => navigate(AppRoutes.MY_WARRANTY_CLAIMS)}
        >
          <RecentClaimsContent
            claims={recentClaims}
            onClickClaim={(id) => navigate(AppRoutes.MY_WARRANTY_CLAIM_DETAILS.replace(":id", id))}
          />
        </DashboardSection>
      </div>

      <section className="flex flex-col items-center gap-6 pt-4">
        <div className="flex flex-col items-center gap-1 text-center">
          <Heading variant="h2" tone="dark">
            Procurtal Recommended for you
          </Heading>
          <Text variant="body" tone="body">
            Handpicked offers from verified suppliers — updated daily
          </Text>
        </div>

        {products.length > 0 && (
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products.map((product) => {
              const discount = computeDiscount(product);
              const stockLabel = getStockLabel(product);
              const wishlistVariantId = getWishlistVariantId(product);
              const navigateToDetails = () =>
                navigate(AppRoutes.PRODUCT_DETAILS.replace(":id", String(product.id)));
              return (
                <div
                  key={product.id}
                  onClick={navigateToDetails}
                  className="h-full cursor-pointer"
                >
                  <ProductCard
                    product={product}
                    discountLabel={discount ? `${discount}% off` : undefined}
                    originalPrice={discount ? (product.standardPrice ?? undefined) : undefined}
                    stockLabel={stockLabel}
                    isInWishlist={wishlistVariantId != null && wishlistIds.includes(wishlistVariantId)}
                    onToggleWishlist={
                      wishlistVariantId != null
                        ? () => void toggleWishlist(wishlistVariantId)
                        : undefined
                    }
                    onViewDetails={navigateToDetails}
                  />
                </div>
              );
            })}
          </div>
        )}

        <Button variant="primary" onClick={() => navigate(AppRoutes.PRODUCTS)}>
          Explore Our Deals
        </Button>
      </section>
    </div>
  );
}

function DashboardSection({
  title,
  badge,
  onViewAll,
  children,
}: {
  title: string;
  badge?: string;
  onViewAll?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col gap-4 rounded-2xl border border-borderDefault bg-bgWhite p-6 max-md:p-4 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Heading variant="h3" tone="dark">{title}</Heading>
          {badge && (
            <span className="rounded-full bg-accentAmberTintBg px-2.5 py-0.5 text-xs font-medium text-accentAmberDark">
              {badge}
            </span>
          )}
        </div>
        {onViewAll && (
          <Button variant="underlinedGhostDark" size="sm" onClick={onViewAll}>
            View All
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

interface PreviewSaleSectionProps {
  sales: SaleDto[];
  isLoading: boolean;
  kind: "order" | "quotation";
  emptyIcon: ComponentType<IconProps>;
  emptyTitle: string;
  onPreview: (id: number) => void;
}

function PreviewSaleSection({
  sales,
  isLoading,
  kind,
  emptyIcon,
  emptyTitle,
  onPreview,
}: PreviewSaleSectionProps) {
  if (isLoading) {
    return (
      <ShimmerLoader message={kind === "order" ? "Loading orders…" : "Loading quotations…"}>
        <div className="flex flex-col gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Shimmer key={i} className="h-[68px] w-full" />
          ))}
        </div>
      </ShimmerLoader>
    );
  }

  if (sales.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        className="!py-4"
      />
    );
  }

  return (
    <>
      {sales.map((sale) => (
        <PreviewSaleRow key={sale.id} sale={sale} kind={kind} onPreview={onPreview} />
      ))}
    </>
  );
}

function PreviewSaleRow({
  sale,
  kind,
  onPreview,
}: {
  sale: SaleDto;
  kind: "order" | "quotation";
  onPreview: (id: number) => void;
}) {
  // Order lines aren't on SaleDto — fetch the first order line image lazily.
  const { data: details } = useSaleDetails(sale.id);
  const firstImage = pickPreviewImage(details);
  const itemCount = sale.orderLineIds?.length ?? 0;
  const currency = sale.currency?.name ?? "QAR";
  const status = deriveOrderStatus(sale);

  const subtitle =
    kind === "order"
      ? `${itemCount} item${itemCount === 1 ? "" : "s"} · ${formatStatus(status)}`
      : `${itemCount} item${itemCount === 1 ? "" : "s"}`;

  const value =
    kind === "order"
      ? dollarFormat(sale.amountTotal ?? 0, currency)
      : undefined;

  const actionLabel =
    kind === "order" && status === "delivered"
      ? "Confirm Receipt"
      : kind === "order"
        ? "Preview Order"
        : "Preview quotation";

  return (
    <PreviewListRow
      thumbnail={<PreviewThumbnail image={firstImage} />}
      title={`${kind === "order" ? "Order" : "Quotation"} ${sale.name}`}
      subtitle={subtitle}
      value={value}
      actionLabel={actionLabel}
      onClick={() => onPreview(sale.id)}
    />
  );
}

function PreviewThumbnail({ image }: { image?: string | null }) {
  if (!image) return <div className="size-full bg-bgLight" />;
  return <Image image={image} alt="" size="w-full h-full" showPlaceholder />;
}

function pickPreviewImage(details: SaleDetailsDto | undefined): string | null {
  return details?.orderLines?.find((l) => l.image)?.image ?? null;
}

const CLAIM_TYPE_LABELS: Record<string, string> = {
  PRODUCT_DEFECT: "Product defect",
  MISSING_ITEMS: "Missing items",
  DAMAGED_IN_TRANSIT: "Damaged in transit",
  WRONG_ITEM: "Wrong item",
  EXPIRED_PRODUCT: "Expired product",
  OTHER: "Other",
};

function RecentClaimsContent({
  claims,
  onClickClaim,
}: {
  claims: WarrantyClaimDto[];
  onClickClaim: (id: string) => void;
}) {
  if (claims.length === 0) {
    return (
      <EmptyState
        icon={IconShieldCheck}
        title="No warranty claims filed"
        className="!py-4"
      />
    );
  }
  return (
    <>
      {claims.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onClickClaim(c.id)}
          className="group flex w-full items-center gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-4 py-3 text-left transition-colors hover:border-accentCyan"
        >
          <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-bgLight">
            <IconShieldCheck size={20} className="text-textMuted" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Text variant="bodyBold" tone="dark" className="truncate">
              Claim #{c.id.slice(0, 8).toUpperCase()}
            </Text>
            <Text variant="bodySm" tone="muted">
              {CLAIM_TYPE_LABELS[c.claimType] ?? c.claimType} · {formatRelativeTime(c.createdAt)}
            </Text>
          </div>
          <ClaimStatusChip status={c.status} />
        </button>
      ))}
    </>
  );
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

function RecentNotificationsContent({
  notifications,
  onClickNotification,
}: {
  notifications: NotificationDto[];
  onClickNotification: (n: NotificationDto) => void;
}) {
  if (notifications.length === 0) {
    return (
      <EmptyState
        icon={IconBell}
        title="You're all caught up"
        className="!py-4"
      />
    );
  }
  return (
    <>
      {notifications.map((n) => (
        <button
          key={n.id}
          type="button"
          onClick={() => onClickNotification(n)}
          className={clsx(
            "flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-bgLight",
            n.readAt === null && "bg-accentGreenTintBg",
          )}
        >
          <span
            className={clsx(
              "mt-2 size-2 shrink-0 rounded-full",
              n.readAt === null ? "bg-accentGreenDark" : "bg-transparent",
            )}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Text variant="bodyBold" tone="dark" className="truncate">{n.title}</Text>
            <Text variant="bodySm" tone="muted" className="line-clamp-1">{n.body}</Text>
          </div>
          <Text variant="bodyXs" tone="muted" className="mt-0.5 shrink-0">
            {formatRelativeTime(n.createdAt)}
          </Text>
        </button>
      ))}
    </>
  );
}

function formatStatus(status: string): string {
  switch (status) {
    case "delivered": return "Delivered";
    case "inTransit": return "In transit";
    case "processing": return "Processing";
    case "validated": return "Validated";
    case "cancelled": return "Cancelled";
    case "rejected": return "Rejected";
    default: return status;
  }
}

export default DashboardPage;
